import { ISearchResult, SearchResultModel } from '@/_types/search'

// ============================================================================
// URL ID Strategy Map
// ============================================================================
type UrlIdStrategy = (result: ISearchResult) => string

const URL_ID_STRATEGY_MAP: Record<SearchResultModel, UrlIdStrategy> = {
	[SearchResultModel.Ambulance]: ({ slug }) => `/ambulancie/${slug}`,
	[SearchResultModel.BlogPost]: ({ slug }) => `/clanky/${slug}`,
	[SearchResultModel.Employee]: ({ slug }) => `/tim/${slug}`,
	[SearchResultModel.AnnualProgram]: ({ id }) => `/rocne-programy#program-${id}`,
	[SearchResultModel.PriceListPromo]: () => `/akciova-ponuka`,
	[SearchResultModel.PreventiveExamination]: ({ id, extras }) =>
		`/preventivne-prehliadky/${extras?.category?.slug}#preventivna-prehliadka-${id}`,
	[SearchResultModel.CreditProgram]: () => `/medante-kredit`,
	[SearchResultModel.PriceListItem]: ({ extras }) =>
		`/ambulancie/${extras?.ambulance?.slug}#cennik`,
	[SearchResultModel.PriceListGroup]: ({ slug, extras }) =>
		`/ambulancie/${extras?.ambulance?.slug}/sluzby/${slug}`
}

// ============================================================================
// Matched Field Name Map
// ============================================================================
// Defines which field to use as the primary title/name for each model
const MATCHED_FIELD_NAME_MAP: Record<SearchResultModel, string> = {
	[SearchResultModel.Ambulance]: 'name',
	[SearchResultModel.BlogPost]: 'title',
	[SearchResultModel.Employee]: 'fullName',
	[SearchResultModel.AnnualProgram]: 'name',
	[SearchResultModel.PriceListPromo]: 'name',
	[SearchResultModel.PriceListItem]: 'name',
	[SearchResultModel.PriceListGroup]: 'name',
	[SearchResultModel.PreventiveExamination]: 'name',
	[SearchResultModel.CreditProgram]: 'name'
}

// ============================================================================
// Matched Field Priority Map
// ============================================================================
// Defines the order of fields to check for displaying as description
const MATCHED_FIELD_PRIORITY_MAP: Record<SearchResultModel, string[]> = {
	[SearchResultModel.Ambulance]: ['description', 'heroText'],
	[SearchResultModel.BlogPost]: ['description'],
	[SearchResultModel.Employee]: ['position', 'email'],
	[SearchResultModel.AnnualProgram]: [],
	[SearchResultModel.PriceListPromo]: [],
	[SearchResultModel.PreventiveExamination]: ['description'],
	[SearchResultModel.CreditProgram]: ['description'],
	[SearchResultModel.PriceListItem]: ['description'],
	[SearchResultModel.PriceListGroup]: ['text']
}

// ============================================================================
// Model Name Map
// ============================================================================
// Defines the name of the model for each model
const MODEL_NAME_MAP: Record<SearchResultModel, string> = {
	[SearchResultModel.Ambulance]: 'Ambulancia',
	[SearchResultModel.BlogPost]: 'Článok',
	[SearchResultModel.Employee]: 'Člen tímu',
	[SearchResultModel.AnnualProgram]: 'Ročný program',
	[SearchResultModel.PriceListPromo]: 'Akciová ponuka',
	[SearchResultModel.PreventiveExamination]: 'Preventívna prehliadka',
	[SearchResultModel.CreditProgram]: 'Medante Kredit',
	[SearchResultModel.PriceListItem]: 'Cenníková položka',
	[SearchResultModel.PriceListGroup]: 'Služby'
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Strips HTML mark, b, and strong tags from a string (used for slug extraction and plain text length calculation)
 */
function stripMarkTags(text: string): string {
	return String(text).replace(/<\/?mark>/g, '')
}

/**
 * Truncates text to a maximum length and adds ellipsis
 * Keeps mark tags intact for highlighting.
 * If a <mark>...<mark> match exists near the edge, truncate so that the match is in the middle.
 */
function truncateText(text: string, maxLength: number = 150): string {
	if (!text) return ''

	// Remove <b> and <strong> tags from the input text first, but keep <mark> tags
	text = text.replace(/<\/?b>/g, '').replace(/<\/?strong>/g, '')

	const textWithoutMarks = stripMarkTags(text)

	if (textWithoutMarks.length <= maxLength) {
		return text // Return original with marks (but without b/strong tags)
	}

	const markOpenTag = '<mark>'
	const markCloseTag = '</mark>'

	// Find location of the first <mark>...</mark>
	const firstMarkStart = text.indexOf(markOpenTag)
	const firstMarkEnd = text.indexOf(markCloseTag)

	let markContentStart = -1
	let markContentEnd = -1

	if (firstMarkStart !== -1 && firstMarkEnd !== -1) {
		markContentStart = firstMarkStart + markOpenTag.length
		markContentEnd = firstMarkEnd
	}

	// Extract the plain string to compute the mark location in terms of plain indices
	const getPlainToHtmlIndices = (html: string) => {
		const arr: Array<{ plainIdx: number; htmlStart: number; htmlEnd: number }> = []
		let i = 0,
			j = 0
		while (i < html.length) {
			if (html.substring(i, i + 6) === markOpenTag) {
				i += 6
				continue
			}
			if (html.substring(i, i + 7) === markCloseTag) {
				i += 7
				continue
			}
			// Skip <b> and </b> tags
			if (html.substring(i, i + 3) === '<b>') {
				i += 3
				continue
			}
			if (html.substring(i, i + 4) === '</b>') {
				i += 4
				continue
			}
			// Skip <strong> and </strong> tags
			if (html.substring(i, i + 8) === '<strong>') {
				i += 8
				continue
			}
			if (html.substring(i, i + 9) === '</strong>') {
				i += 9
				continue
			}
			arr.push({ plainIdx: j, htmlStart: i, htmlEnd: i + 1 })
			i++
			j++
		}
		return arr
	}

	const mapping = getPlainToHtmlIndices(text)
	const totalPlainLength = mapping.length

	// If no <mark>, use the standard truncation from the start
	if (markContentStart === -1 || markContentEnd === -1) {
		// Truncate while preserving mark tags and removing b/strong tags
		let result = ''
		let plainTextLength = 0
		let i = 0

		while (i < text.length && plainTextLength < maxLength) {
			if (text.substring(i, i + 6) === markOpenTag) {
				result += markOpenTag
				i += 6
				continue
			}
			if (text.substring(i, i + 7) === markCloseTag) {
				result += markCloseTag
				i += 7
				continue
			}
			// Skip <b> and </b> tags (don't include in result)
			if (text.substring(i, i + 3) === '<b>') {
				i += 3
				continue
			}
			if (text.substring(i, i + 4) === '</b>') {
				i += 4
				continue
			}
			// Skip <strong> and </strong> tags (don't include in result)
			if (text.substring(i, i + 8) === '<strong>') {
				i += 8
				continue
			}
			if (text.substring(i, i + 9) === '</strong>') {
				i += 9
				continue
			}
			result += text[i]
			plainTextLength++
			i++
		}

		// Close any unclosed mark tags
		const openMarks = (result.match(/<mark>/g) || []).length
		const closeMarks = (result.match(/<\/mark>/g) || []).length
		if (openMarks > closeMarks) {
			result += '</mark>'
		}

		return result.trim() + '...'
	}

	// There is a <mark>...</mark> in the input - we want the match to be visible and centered if it's near the edge
	// Find the match position in the plain string, so we know where to center
	const markPlainStart = mapping.find(m => m.htmlStart === markContentStart)?.plainIdx ?? 0
	// markContentEnd points to the '<' of '</mark>', so we need to find the last character before it
	const markPlainEnd =
		mapping.findLast(m => m.htmlStart < markContentEnd)?.plainIdx ?? markPlainStart

	const matchLength = markPlainEnd - markPlainStart + 1
	const context = Math.floor((maxLength - matchLength) / 2)

	// Calculate clamp for centered window
	let windowStartPlain = Math.max(0, markPlainStart - context)
	let windowEndPlain = Math.min(totalPlainLength, windowStartPlain + maxLength)

	// If match is near the end, show the ending context
	if (windowEndPlain === totalPlainLength) {
		windowStartPlain = Math.max(0, totalPlainLength - maxLength)
	}
	// If match is near the start, show the starting context
	else if (windowStartPlain === 0) {
		windowEndPlain = Math.min(totalPlainLength, maxLength)
	}

	// Find corresponding html indices for slicing
	const htmlStart = mapping[windowStartPlain]?.htmlStart ?? 0
	const htmlEnd = mapping[windowEndPlain - 1]?.htmlEnd ?? text.length

	let truncated = text.slice(htmlStart, htmlEnd)

	// Remove <b> and <strong> tags from truncated text
	truncated = truncated.replace(/<\/?b>/g, '').replace(/<\/?strong>/g, '')

	// Ensure valid mark tags:
	const openMarks = (truncated.match(/<mark>/g) || []).length
	const closeMarks = (truncated.match(/<\/mark>/g) || []).length
	if (openMarks > closeMarks) {
		truncated += '</mark>'
	}
	if (closeMarks > openMarks) {
		truncated = '<mark>' + truncated
	}

	const addPrefix = windowStartPlain > 0
	const addSuffix = windowEndPlain < totalPlainLength

	return (addPrefix ? '...' : '') + truncated.trim() + (addSuffix ? '...' : '')
}

/**
 * Gets the name/title field from matchedFields based on model configuration
 * Falls back to result.title if matched field is not found
 */
export function getMatchedFieldName(result: ISearchResult): string {
	const nameField = MATCHED_FIELD_NAME_MAP[result.model]

	if (nameField && result.matchedFields[nameField]) {
		const value = result.matchedFields[nameField]
		if (typeof value === 'string' && value.trim()) {
			return value
		}
	}

	// Fallback to result.title
	return result.title
}

export function getDefaultDescription(result: ISearchResult): string | null {
	if (!result.description) return null

	// @ts-expect-error: Property 'blocks' does not exist on type 'string'
	if (result.description?.blocks) {
		return null
	}

	if (Array.isArray(result.description) && result.description) {
		return truncateText(result.description[0])
	}

	return truncateText(result.description as string)
}

/**
 * Gets the first matched field value based on priority
 * Returns HTML string with mark tags preserved
 */
export function getMatchedFieldDescription(result: ISearchResult): string | null {
	try {
		const priorities = MATCHED_FIELD_PRIORITY_MAP[result.model]
		if (!priorities) return getDefaultDescription(result)

		for (const field of priorities) {
			const value = result.matchedFields[field]

			if (Array.isArray(value) && value.length > 0) {
				const truncatedValue = truncateText(value[0] || '')
				if (truncatedValue?.length) {
					return truncatedValue
				}
			} else if (typeof value === 'string' && value.trim()) {
				// Keep mark tags for highlighting
				return truncateText(value)
			}
		}

		return getDefaultDescription(result)
	} catch (error) {
		console.error('Error in getMatchedFieldDescription:', error)
		return getDefaultDescription(result)
	}
}

/**
 * Generates URL for a search result based on model and ID/slug strategy
 */
export function getSearchResultUrl(result: ISearchResult): string {
	try {
		const strategy = URL_ID_STRATEGY_MAP[result.model]
		return strategy(result)
	} catch (error) {
		console.error('Error in getSearchResultUrl:', error, result)
		return '#'
	}
}

/**
 * Gets the name of the model
 * @param result - Search result
 * @returns Model name
 */
export function getModelName(result: ISearchResult): string {
	return MODEL_NAME_MAP[result.model]
}
